import { format, subDays } from "date-fns";
import AxiosInstance from "@/utils/AxiosInstance";

export type PatientHistoryActivityType =
  | "triage_alert"
  | "notification"
  | "device_reading"
  | "careplan_edit"
  | "nursing_note"
  | "settings";

export type PatientHistoryCategory =
  | "management"
  | "notifications"
  | "clinical"
  | "device"
  | "support"
  | "access";

export type PatientHistoryTitlePart = { text: string; bold?: boolean };

export type PatientHistoryItem = {
  id: string;
  type: PatientHistoryActivityType;
  category: PatientHistoryCategory;
  titleParts: PatientHistoryTitlePart[];
  subtitle?: string;
  occurredAt: string;
  displayTime?: string;
  groupDateKey?: string;
  groupDateLabel?: string;
};

export type PatientHistoryQuery = {
  search?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
};

export type PatientHistoryResult = {
  items: PatientHistoryItem[];
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
};

function unwrapData(payload: unknown): Record<string, unknown> {
  let root: unknown = payload;
  for (let i = 0; i < 3; i++) {
    if (
      root &&
      typeof root === "object" &&
      "data" in (root as object) &&
      (root as { data?: unknown }).data != null &&
      !Array.isArray((root as { data?: unknown }).data)
    ) {
      root = (root as { data: unknown }).data;
    } else {
      break;
    }
  }
  return (root && typeof root === "object" ? root : {}) as Record<string, unknown>;
}

function extractHistoryRows(
  payload: unknown,
): Array<{ row: unknown; dateKey?: string; dateLabel?: string }> {
  const root = unwrapData(payload);

  if (Array.isArray(root.groups)) {
    return root.groups.flatMap((group) => {
      if (!group || typeof group !== "object") return [];
      const groupRecord = group as {
        date?: string;
        dateLabel?: string;
        events?: unknown[];
      };
      const events = Array.isArray(groupRecord.events) ? groupRecord.events : [];
      return events.map((row) => ({
        row,
        dateKey: groupRecord.date,
        dateLabel: groupRecord.dateLabel,
      }));
    });
  }

  const candidates = [
    root.items,
    root.events,
    root.history,
    root.records,
    root.results,
    root.activities,
    root.timeline,
    Array.isArray(payload) ? payload : null,
    Array.isArray(root) ? root : null,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate.map((row) => ({ row }));
    }
  }

  return [];
}

function extractPagination(
  payload: unknown,
  fallbackCount: number,
  query: PatientHistoryQuery,
): Pick<PatientHistoryResult, "page" | "limit" | "total" | "hasMore"> {
  const root = unwrapData(payload);
  const pagination =
    (root.pagination as Record<string, unknown> | undefined) ??
    (root.meta as Record<string, unknown> | undefined) ??
    root;

  const page = Number(pagination.page ?? query.page ?? 1) || 1;
  const limit = Number(pagination.limit ?? query.limit ?? 50) || 50;
  const total = Number(
    pagination.total ??
      pagination.totalCount ??
      pagination.count ??
      fallbackCount,
  );
  const totalPages = Number(pagination.totalPages);

  const safeTotal = Number.isFinite(total) ? total : fallbackCount;
  const hasMore = Number.isFinite(totalPages)
    ? page < totalPages
    : page * limit < safeTotal;

  return {
    page,
    limit,
    total: safeTotal,
    hasMore,
  };
}

function normalizeCategory(raw: unknown): PatientHistoryCategory {
  const value = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

  if (value === "admin") return "management";
  if (value === "account") return "access";
  if (value.includes("management") || value.includes("careplan")) {
    return "management";
  }
  if (value.includes("notification")) return "notifications";
  if (value.includes("clinical") || value.includes("triage") || value.includes("nursing")) {
    return "clinical";
  }
  if (value.includes("device") || value.includes("reading")) return "device";
  if (value.includes("support")) return "support";
  if (value.includes("access")) return "access";
  return "clinical";
}

function normalizeActivityType(
  eventType: unknown,
  icon: unknown,
  category: PatientHistoryCategory,
): PatientHistoryActivityType {
  const iconValue = String(icon ?? "").trim().toLowerCase();
  if (iconValue === "note") return "nursing_note";
  if (iconValue === "document") return "careplan_edit";
  if (iconValue === "settings" || iconValue === "account") return "settings";
  if (iconValue === "bell") return "notification";
  if (iconValue === "activity" || iconValue === "pulse") return "device_reading";
  if (iconValue === "alert") return "triage_alert";

  const value = String(eventType ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

  if (value.includes("triage") && value.includes("alert")) return "triage_alert";
  if (value.includes("notification")) return "notification";
  if (value.includes("device_reading") || value.includes("reading")) {
    return "device_reading";
  }
  if (value.includes("careplan")) return "careplan_edit";
  if (value.includes("nursing") || value.includes("internal_note")) {
    return "nursing_note";
  }
  if (
    value.includes("special_instruction") ||
    value.includes("status_change") ||
    value.includes("patient_created") ||
    value.includes("password")
  ) {
    return "settings";
  }

  switch (category) {
    case "management":
      return "settings";
    case "notifications":
      return "notification";
    case "device":
      return "device_reading";
    case "access":
      return "settings";
    default:
      return "nursing_note";
  }
}

function parseApiTimeLabel(timeLabel: unknown): string | undefined {
  const raw = String(timeLabel ?? "").trim();
  if (!raw) return undefined;

  const parts = raw.split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[parts.length - 2]} ${parts[parts.length - 1]}`;
  }

  return raw;
}

function mapHistoryRow(
  row: unknown,
  index: number,
  group?: { dateKey?: string; dateLabel?: string },
): PatientHistoryItem | null {
  if (!row || typeof row !== "object") return null;
  const record = row as Record<string, unknown>;

  const category = normalizeCategory(record.category);
  const type = normalizeActivityType(record.eventType, record.icon, category);

  const occurredAt = String(record.occurredAt ?? "").trim();
  if (!occurredAt) return null;

  const message = String(record.message ?? "").trim();
  const titleParts: PatientHistoryTitlePart[] = message
    ? [{ text: message }]
    : [{ text: "Activity recorded" }];

  const subtitle = String(record.subMessage ?? "").trim() || undefined;

  return {
    id: String(record.id ?? record._id ?? `${occurredAt}-${index}`),
    type,
    category,
    titleParts,
    subtitle,
    occurredAt,
    displayTime: parseApiTimeLabel(record.timeLabel),
    groupDateKey: group?.dateKey,
    groupDateLabel: group?.dateLabel,
  };
}

export function getPatientHistoryDateRange(
  dateRange: string,
): Pick<PatientHistoryQuery, "startDate" | "endDate"> {
  if (dateRange === "all") return {};

  const days = Number(dateRange) || 30;
  const endDate = format(new Date(), "yyyy-MM-dd");
  const startDate = format(subDays(new Date(), days), "yyyy-MM-dd");
  return { startDate, endDate };
}

export function normalizePatientHistoryResponse(
  payload: unknown,
  query: PatientHistoryQuery,
): PatientHistoryResult {
  const rows = extractHistoryRows(payload);
  const items = rows
    .map(({ row, dateKey, dateLabel }, index) =>
      mapHistoryRow(row, index, { dateKey, dateLabel }),
    )
    .filter(Boolean) as PatientHistoryItem[];

  const pagination = extractPagination(payload, items.length, query);

  return {
    items,
    ...pagination,
  };
}

export type AdminHistoryQuery = PatientHistoryQuery & {
  sourceSheet?: string;
  organizationId?: string;
};

export async function fetchAdminHistory(
  query: AdminHistoryQuery,
): Promise<PatientHistoryResult> {
  const params: Record<string, string | number> = {
    page: query.page ?? 1,
    limit: query.limit ?? 50,
  };

  if (query.search?.trim()) params.search = query.search.trim();
  if (query.startDate) params.startDate = query.startDate;
  if (query.endDate) params.endDate = query.endDate;
  if (query.sourceSheet?.trim()) params.sourceSheet = query.sourceSheet.trim();
  if (query.organizationId?.trim()) {
    params.organizationId = query.organizationId.trim();
  }

  const { data } = await AxiosInstance().get("/admin/history", { params });

  return normalizePatientHistoryResponse(data, query);
}

export async function fetchPatientHistory(
  patientId: string,
  query: PatientHistoryQuery,
): Promise<PatientHistoryResult> {
  const params: Record<string, string | number> = {
    page: query.page ?? 1,
    limit: query.limit ?? 50,
  };

  if (query.search?.trim()) params.search = query.search.trim();
  if (query.startDate) params.startDate = query.startDate;
  if (query.endDate) params.endDate = query.endDate;

  const { data } = await AxiosInstance().get(`/patients/${patientId}/history`, {
    params,
  });

  return normalizePatientHistoryResponse(data, query);
}

export async function fetchCaretakerHistory(
  caretakerId: string,
  query: PatientHistoryQuery,
): Promise<PatientHistoryResult> {
  const params: Record<string, string | number> = {
    page: query.page ?? 1,
    limit: query.limit ?? 50,
  };

  if (query.search?.trim()) params.search = query.search.trim();
  if (query.startDate) params.startDate = query.startDate;
  if (query.endDate) params.endDate = query.endDate;

  const { data } = await AxiosInstance().get(
    `/caretakers/${caretakerId}/history`,
    { params },
  );

  return normalizePatientHistoryResponse(data, query);
}

export async function fetchProviderHistory(
  providerId: string,
  query: PatientHistoryQuery,
): Promise<PatientHistoryResult> {
  const params: Record<string, string | number> = {
    page: query.page ?? 1,
    limit: query.limit ?? 50,
    sourceSheet: "provider",
  };

  if (query.search?.trim()) params.search = query.search.trim();
  if (query.startDate) params.startDate = query.startDate;
  if (query.endDate) params.endDate = query.endDate;

  const { data } = await AxiosInstance().get(
    `/providers/${providerId}/history`,
    { params },
  );

  return normalizePatientHistoryResponse(data, query);
}

export async function fetchPractitionerHistory(
  practitionerId: string,
  query: PatientHistoryQuery,
): Promise<PatientHistoryResult> {
  const params: Record<string, string | number> = {
    page: query.page ?? 1,
    limit: query.limit ?? 50,
    sourceSheet: "practitioner",
  };

  if (query.search?.trim()) params.search = query.search.trim();
  if (query.startDate) params.startDate = query.startDate;
  if (query.endDate) params.endDate = query.endDate;

  const { data } = await AxiosInstance().get(
    `/practitioners/${practitionerId}/history`,
    { params },
  );

  return normalizePatientHistoryResponse(data, query);
}

export type HistoryResourceType =
  | "patient"
  | "caretaker"
  | "provider"
  | "practitioner";

export async function fetchEntityHistory(
  resourceType: HistoryResourceType,
  resourceId: string,
  query: PatientHistoryQuery,
): Promise<PatientHistoryResult> {
  if (resourceType === "caretaker") {
    return fetchCaretakerHistory(resourceId, query);
  }
  if (resourceType === "provider") {
    return fetchProviderHistory(resourceId, query);
  }
  if (resourceType === "practitioner") {
    return fetchPractitionerHistory(resourceId, query);
  }
  return fetchPatientHistory(resourceId, query);
}
